import { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, HistogramSeries } from 'lightweight-charts';
import { createChartOptions, SERIES_CONFIG, VOLUME_SERIES_OPTIONS } from '../utils/chartConfig';
import { fetchKlines, createKlineSocket } from '../utils/binanceData';
import { getBinanceSymbol } from '../utils/chartHelpers';

export function useChartSetup({ coin, timeframe, onPriceUpdate, chartType = 'Candlestick', showVolume = false }) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartVersion, setChartVersion] = useState(0);
  const [data, setData] = useState([]);

  const binanceSymbol = coin ? getBinanceSymbol(coin.symbol) : '';

  const handlePriceUpdate = useCallback((price) => {
    if (onPriceUpdate) onPriceUpdate(price);
  }, [onPriceUpdate]);

  useEffect(() => {
    if (!binanceSymbol || !chartContainerRef.current) return;

    setLoading(true);
    setError(null);

    const config = SERIES_CONFIG[chartType] || SERIES_CONFIG.Candlestick;
    const chart = createChart(
      chartContainerRef.current,
      createChartOptions(chartContainerRef.current.clientWidth)
    );

    const series = chart.addSeries(config.constructor, config.options);
    let volumeSeries = null;
    if (showVolume) {
      volumeSeries = chart.addSeries(HistogramSeries, VOLUME_SERIES_OPTIONS);
      volumeSeriesRef.current = volumeSeries;
      volumeSeries.priceScale().applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
      });
    }

    chartRef.current = chart;
    seriesRef.current = series;
    setChartVersion(v => v + 1);

    const onResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', onResize);

    let ws = null;
    let isLoadingMore = false;
    let oldestTime = null;
    let currentData = [];

    const isSingleValue = chartType === 'Line' || chartType === 'Area';
    const mapCandle = isSingleValue
      ? (d) => ({ time: d.time, value: d.close })
      : (d) => d;

    const loadMoreHistory = async () => {
      if (isLoadingMore || !oldestTime) return;
      isLoadingMore = true;
      try {
        const endTime = oldestTime * 1000 - 1;
        const olderData = await fetchKlines(binanceSymbol, timeframe, endTime);
        if (olderData.length > 0) {
          oldestTime = olderData[0].time;
          currentData = [...olderData, ...currentData];
          setData([...currentData]);
          series.setData(currentData.map(mapCandle));
          if (volumeSeries) {
            volumeSeries.setData(currentData.map(d => ({
              time: d.time,
              value: d.volume,
              color: d.close >= d.open ? 'rgba(14, 203, 129, 0.4)' : 'rgba(246, 70, 93, 0.4)',
            })));
          }
        }
      } catch (err) {
        console.error('Failed to load older data:', err);
      } finally {
        isLoadingMore = false;
      }
    };

    const onVisibleLogicalRangeChanged = (newVisibleLogicalRange) => {
      if (newVisibleLogicalRange !== null && newVisibleLogicalRange.from < 50) {
        loadMoreHistory();
      }
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChanged);

    fetchKlines(binanceSymbol, timeframe)
      .then(candleData => {
        if (candleData.length > 0) {
          oldestTime = candleData[0].time;
        }
        currentData = candleData;
        setData([...currentData]);
        series.setData(candleData.map(mapCandle));
        if (volumeSeries) {
          volumeSeries.setData(candleData.map(d => ({
            time: d.time,
            value: d.volume,
            color: d.close >= d.open ? 'rgba(14, 203, 129, 0.4)' : 'rgba(246, 70, 93, 0.4)',
          })));
        }
        chart.timeScale().scrollToPosition(0, false);
        if (candleData.length) handlePriceUpdate(candleData[candleData.length - 1].close);
        setLoading(false);

        ws = createKlineSocket(binanceSymbol, timeframe, (candle) => {
          series.update(isSingleValue
            ? { time: candle.time, value: candle.close }
            : candle
          );
          if (volumeSeries) {
            volumeSeries.update({
              time: candle.time,
              value: candle.volume,
              color: candle.close >= candle.open ? 'rgba(14, 203, 129, 0.4)' : 'rgba(246, 70, 93, 0.4)',
            });
          }
          // Update the last candle in our local array too so indicators stay consistent
          const lastIdx = currentData.length - 1;
          if (lastIdx >= 0 && currentData[lastIdx].time === candle.time) {
            currentData[lastIdx] = candle;
          } else {
            currentData.push(candle);
          }
          setData([...currentData]);
          handlePriceUpdate(candle.close);
        });
      })
      .catch(err => {
        console.error('Failed to load charts:', err);
        setError('Error fetching historical chart data from Binance API.');
        setLoading(false);
      });

    return () => {
      window.removeEventListener('resize', onResize);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChanged);
      if (ws) ws.close();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, [binanceSymbol, timeframe, chartType, showVolume, handlePriceUpdate]);

  return { chartRef, seriesRef, chartContainerRef, volumeSeriesRef, loading, error, chartVersion, data };
}
